<template>
    <div
        class="mx-auto w-full max-w-md rounded-2xl border-hairline border-theme-border bg-theme-card p-3 shadow-sm"
    >
        <!-- Una sola publicación: no tiene sentido armar un mosaico, y el
        thumbnail (og:image que expone Instagram para "link preview") viene
        recortado a cuadrado y en baja resolución por el propio Instagram
        — no algo que se arregle con CSS. En su lugar, el embed real
        (iframe), igual que en modo "único": se ve completo y a full
        resolución. -->
        <MediaSegment
            v-if="visibleItems.length === 1"
            :media-type="mediaType"
            :link="visibleItems[0].link"
            :preview-only="previewOnly"
        />

        <!-- TikTok con 2+ videos: abanico de tarjetas superpuestas (la
        miniatura de perfil que usa la propia app de TikTok), en vez del
        mosaico tipo grilla de Instagram — encaja mejor con thumbnails
        verticales. -->
        <div
            v-else-if="isTiktokStack"
            class="flex items-center justify-center py-4"
        >
            <a
                v-for="(item, index) in stackItems"
                :key="item.id"
                :href="item.link"
                target="_blank"
                rel="noopener noreferrer"
                class="relative -mx-6 w-32 flex-shrink-0 overflow-hidden rounded-2xl bg-theme-hover shadow-lg sm:w-36"
                :style="stackCardStyle(index)"
                @click="onClick"
            >
                <img
                    v-if="hasThumbnail(item)"
                    :src="item.thumbnailUrl"
                    alt=""
                    class="absolute inset-0 h-full w-full object-cover"
                    @error="onImageError(item)"
                />
                <span
                    v-else
                    class="absolute inset-0 flex items-center justify-center text-2xl text-theme-muted"
                >
                    {{ platformIcon }}
                </span>
                <div
                    v-if="
                        index === stackItems.length - 1 &&
                        stackOverflowCount > 0
                    "
                    class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-semibold text-white"
                >
                    +{{ stackOverflowCount }}
                </div>
            </a>
        </div>

        <!-- 2 o más (resto de plataformas): mosaico (celdas recortadas a
        la fuerza para armar la grilla, igual que la grilla de perfil de
        Instagram). -->
        <div
            v-else-if="visibleItems.length > 1"
            class="grid gap-1 overflow-hidden rounded-xl"
            :style="gridContainerStyle"
        >
            <a
                v-for="(item, index) in visibleItems"
                :key="item.id"
                :href="item.link"
                target="_blank"
                rel="noopener noreferrer"
                class="relative overflow-hidden rounded-md bg-theme-hover"
                :style="cellStyle(index)"
                @click="onClick"
            >
                <img
                    v-if="hasThumbnail(item)"
                    :src="item.thumbnailUrl"
                    alt=""
                    class="absolute inset-0 h-full w-full object-cover"
                    @error="onImageError(item)"
                />
                <span
                    v-else
                    class="absolute inset-0 flex items-center justify-center text-lg text-theme-muted"
                >
                    {{ platformIcon }}
                </span>
                <div
                    v-if="isLastVisible(index) && overflowCount > 0"
                    class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-semibold text-white"
                >
                    +{{ overflowCount }}
                </div>
            </a>
        </div>
        <div
            v-else
            class="flex h-32 items-center justify-center rounded-xl border border-dashed border-theme-border text-xs text-theme-muted"
        >
            Agrega publicaciones para mostrarlas aquí
        </div>

        <div class="mt-3 text-center">
            <span class="text-base font-semibold text-theme-base">
                {{ platformLabel }}
            </span>
        </div>
    </div>
</template>

<script>
import {
    SOCIAL_PLATFORM_ICONS,
    SOCIAL_PLATFORM_LABELS
} from "@/common/social-embeds";

import MediaSegment from "@/components/catalog/MediaSegment.vue";

const MAX_VISIBLE_ITEMS = 5;
const STACK_MAX_ITEMS = 3;

// Abanico de tarjetas para TikTok: la del centro queda al frente, recta y
// más grande; las de los costados quedan detrás, giradas hacia afuera y
// un poco más abajo/chicas. Todo relativo a la posición respecto al
// centro para que funcione igual con 2 o 3 tarjetas visibles.
const STACK_ROTATION_DEG = 10;
const STACK_OFFSET_Y_PX = 16;
const STACK_SCALE_STEP = 0.08;

// Mosaico adaptativo para 2+ publicaciones, siempre llenando el rectángulo
// completo (sin celdas vacías): 2 lado a lado; 3 son 2 arriba + 1 ancha
// abajo; 4 y 5 son 1 celda grande a la izquierda (ocupa las 2 filas) + el
// resto a la derecha, como en el widget de referencia — con 4, la última
// de la derecha ocupa las 2 columnas de abajo (no deja hueco). Todo en
// unidades relativas (fr / aspect-ratio) para que escale fluido a
// cualquier ancho de pantalla. Con 1 sola publicación no se usa mosaico —
// ver el template.
const MOSAIC_CELLS = {
    2: [
        { gridColumn: "1", gridRow: "1" },
        { gridColumn: "2", gridRow: "1" }
    ],
    3: [
        { gridColumn: "1", gridRow: "1" },
        { gridColumn: "2", gridRow: "1" },
        { gridColumn: "1 / span 2", gridRow: "2" }
    ],
    4: [
        { gridColumn: "1", gridRow: "1 / span 2" },
        { gridColumn: "2", gridRow: "1" },
        { gridColumn: "3", gridRow: "1" },
        { gridColumn: "2 / span 2", gridRow: "2" }
    ],
    5: [
        { gridColumn: "1", gridRow: "1 / span 2" },
        { gridColumn: "2", gridRow: "1" },
        { gridColumn: "3", gridRow: "1" },
        { gridColumn: "2", gridRow: "2" },
        { gridColumn: "3", gridRow: "2" }
    ]
};

const MOSAIC_COLUMNS = {
    2: "1fr 1fr",
    3: "1fr 1fr",
    4: "2fr 1fr 1fr",
    5: "2fr 1fr 1fr"
};

const MOSAIC_ASPECT_RATIO = {
    2: "2 / 1"
};

export default {
    name: "SocialFeedSegment",
    components: { MediaSegment },
    props: {
        mediaType: { type: String, required: true },
        items: { type: Array, default: () => [] },
        previewOnly: { type: Boolean, default: false }
    },
    data() {
        return {
            brokenItemIds: []
        };
    },
    computed: {
        platformLabel() {
            return SOCIAL_PLATFORM_LABELS[this.mediaType] || "Publicación";
        },
        platformIcon() {
            return SOCIAL_PLATFORM_ICONS[this.mediaType] || "🔗";
        },
        visibleItems() {
            return this.items.slice(0, MAX_VISIBLE_ITEMS);
        },
        overflowCount() {
            return Math.max(0, this.items.length - MAX_VISIBLE_ITEMS);
        },
        isTiktokStack() {
            return this.mediaType === "tiktok" && this.visibleItems.length > 1;
        },
        stackItems() {
            return this.visibleItems.slice(0, STACK_MAX_ITEMS);
        },
        stackOverflowCount() {
            return Math.max(0, this.items.length - this.stackItems.length);
        },
        gridContainerStyle() {
            const count = this.visibleItems.length;
            return {
                gridTemplateColumns: MOSAIC_COLUMNS[count],
                gridTemplateRows: count > 2 ? "1fr 1fr" : "1fr",
                aspectRatio: MOSAIC_ASPECT_RATIO[count] || "4 / 3"
            };
        }
    },
    methods: {
        stackCardStyle(index) {
            const total = this.stackItems.length;
            const center = (total - 1) / 2;
            const offset = index - center;
            const distance = Math.abs(offset);
            return {
                aspectRatio: "3 / 4",
                transform: `translateY(${
                    distance * STACK_OFFSET_Y_PX
                }px) rotate(${offset * STACK_ROTATION_DEG}deg) scale(${
                    1 - distance * STACK_SCALE_STEP
                })`,
                zIndex: total - Math.round(distance)
            };
        },
        cellStyle(index) {
            const layout = MOSAIC_CELLS[this.visibleItems.length];
            return layout?.[index] || { gridColumn: "1", gridRow: "1" };
        },
        isLastVisible(index) {
            return index === this.visibleItems.length - 1;
        },
        hasThumbnail(item) {
            return !!item.thumbnailUrl && !this.brokenItemIds.includes(item.id);
        },
        onImageError(item) {
            if (!this.brokenItemIds.includes(item.id)) {
                this.brokenItemIds = [...this.brokenItemIds, item.id];
            }
        },
        onClick(event) {
            if (this.previewOnly) event.preventDefault();
        }
    }
};
</script>
